import GuideLayout from "../components/GuideLayout";
import { useLang } from "../i18n/LanguageContext";
import { faqPageLd } from "../lib/schema";

const HERO =
  "https://static.prod-images.emergentagent.com/jobs/c98acbe0-6e23-474e-adcd-5ab166743bcb/images/aa61e208e560b2bc15075a01f3d79dfedcf86fe880788cdf7ff3ea7248a420e9.jpeg";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Private Cinque Terre day trip with chauffeur",
  name: "Private Cinque Terre Tour",
  provider: { "@id": "https://yourblacklimo.com/#business" },
  areaServed: ["Cinque Terre", "La Spezia", "Portovenere", "Lucca", "Pisa", "Tuscany"],
  description:
    "A private, chauffeur-driven day to the Cinque Terre from Tuscany — the five villages, Portovenere and the Ligurian coast, combining a Mercedes V-Class with the scenic coastal train or boat.",
  url: "https://yourblacklimo.com/cinque-terre-private-tour",
};

const CONTENT = {
  en: {
    seoTitle: "Private Cinque Terre Tour | Chauffeur Day Trip from Tuscany — Your Black Limo",
    seoDesc:
      "A private, chauffeur-driven day to the Cinque Terre — Monterosso, Vernazza, Manarola, Riomaggiore and Portovenere. Door-to-door from Lucca, Pisa, Florence or the cruise ports in a Mercedes V-Class.",
    overline: "Cinque Terre Day Trip",
    h1: "Private Cinque Terre Tour from Tuscany",
    intro:
      "Five pastel villages clinging to the Ligurian cliffs. Because cars cannot enter the Cinque Terre, we combine a private Mercedes V-Class with the scenic coastal train or a boat — so you enjoy the drama of the coast without a single logistical worry.",
    heroAlt: "Colourful houses of Manarola clinging to the Cinque Terre coast",
    blocks: [
      {
        title: "The five villages",
        body: "Monterosso's beaches, Vernazza's harbour, Corniglia on its terraces, Manarola at golden hour and Riomaggiore's steep lanes. We help you choose two or three to savour rather than rushing all five, so the day stays unhurried and memorable.",
      },
      {
        title: "How the day flows",
        body: "We drive you to La Spezia or Levanto, guide you onto the coastal train that hops between the villages in minutes, and are waiting when you return. On calm days a boat offers the finest views from the water, which the client is welcome to arrange directly.",
      },
    ],
    cards: {
      overline: "Highlights",
      title: "Along the Ligurian coast",
      items: [
        { title: "Vernazza & Monterosso", body: "The postcard harbour and the only village with a true beach." },
        { title: "Manarola & Riomaggiore", body: "The most photographed villages, glowing at sunset." },
        { title: "Portovenere", body: "The elegant 'sixth village', with Byron's grotto and a Gothic seafront church." },
        { title: "Boat or train", body: "The car-free coast, enjoyed the smart way — by rail between villages or by sea." },
      ],
    },
    note: {
      title: "The practical side, handled",
      body:
        "The Cinque Terre are car-free, so the smartest visit pairs a private driver with the coastal train. We handle the timing, meet you at each end and keep the day relaxed. Ideal as a shore excursion from La Spezia or a full day from Lucca, Pisa or Florence. Fully licensed NCC, English-speaking chauffeur, Mercedes V-Class for up to seven guests.",
    },
    cta: {
      title: "Plan your Cinque Terre day",
      secondaryLabel: "Cruise transfers",
      secondaryHref: "/livorno-cruise-transfers",
      primaryLabel: "Request a quote",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Questions",
      title: "Frequently asked questions",
      items: [
        { q: "Can you drive into the Cinque Terre villages?", a: "The villages are car-free, so we drive to La Spezia or Levanto and use the scenic coastal train — the fastest, most relaxed way to hop between them." },
        { q: "How far is it from Tuscany?", a: "Around 90 minutes from Lucca or Pisa, and very close from the port of La Spezia — perfect as a shore excursion or a full day." },
        { q: "How many villages can we see?", a: "We usually suggest two or three to enjoy properly, rather than rushing all five in a day." },
        { q: "Can we go by boat?", a: "On calm days, yes — a boat offers the most spectacular views of the villages from the sea, and you're welcome to arrange one directly." },
      ],
    },
  },
  it: {
    seoTitle: "Tour Privato delle Cinque Terre | Escursione con Autista dalla Toscana — Your Black Limo",
    seoDesc:
      "Una giornata privata alle Cinque Terre con autista — Monterosso, Vernazza, Manarola, Riomaggiore e Portovenere. Porta a porta da Lucca, Pisa, Firenze o dai porti crociere in Mercedes Classe V.",
    overline: "Escursione alle Cinque Terre",
    h1: "Tour Privato delle Cinque Terre dalla Toscana",
    intro:
      "Cinque borghi pastello aggrappati alle scogliere liguri. Poiché le auto non possono entrare nelle Cinque Terre, uniamo una Mercedes Classe V privata al panoramico treno costiero o a una barca — così godi della scenografia della costa senza il minimo pensiero logistico.",
    heroAlt: "Le case colorate di Manarola aggrappate alla costa delle Cinque Terre",
    blocks: [
      {
        title: "I cinque borghi",
        body: "Le spiagge di Monterosso, il porticciolo di Vernazza, Corniglia sulle sue terrazze, Manarola all'ora dorata e i vicoli ripidi di Riomaggiore. Ti aiutiamo a sceglierne due o tre da assaporare invece di correre tra tutti e cinque, così la giornata resta tranquilla e indimenticabile.",
      },
      {
        title: "Come si svolge la giornata",
        body: "Ti portiamo a La Spezia o Levanto, ti accompagniamo sul treno costiero che collega i borghi in pochi minuti e ti aspettiamo al ritorno. Nelle giornate calme una barca offre le viste più belle dal mare, che il cliente può prenotare direttamente.",
      },
    ],
    cards: {
      overline: "Da non perdere",
      title: "Lungo la costa ligure",
      items: [
        { title: "Vernazza e Monterosso", body: "Il porticciolo da cartolina e l'unico borgo con una vera spiaggia." },
        { title: "Manarola e Riomaggiore", body: "I borghi più fotografati, che si accendono al tramonto." },
        { title: "Portovenere", body: "L'elegante 'sesto borgo', con la grotta di Byron e la chiesa gotica sul mare." },
        { title: "Barca o treno", body: "La costa senz'auto, vissuta nel modo intelligente — in treno tra i borghi o via mare." },
      ],
    },
    note: {
      title: "Il lato pratico, risolto",
      body:
        "Le Cinque Terre sono senza auto, quindi la visita più intelligente unisce l'autista privato al treno costiero. Gestiamo noi gli orari, ti accogliamo a ogni tappa e manteniamo la giornata rilassata. Ideale come escursione a terra da La Spezia o come giornata intera da Lucca, Pisa o Firenze. NCC regolarmente autorizzato, autista che parla inglese, Mercedes Classe V fino a sette ospiti.",
    },
    cta: {
      title: "Organizza la tua giornata alle Cinque Terre",
      secondaryLabel: "Transfer crociere",
      secondaryHref: "/livorno-cruise-transfers",
      primaryLabel: "Richiedi un preventivo",
      primaryHref: "/#contact",
    },
    faq: {
      overline: "Domande",
      title: "Domande frequenti",
      items: [
        { q: "Potete entrare in auto nei borghi delle Cinque Terre?", a: "I borghi sono senza auto, quindi guidiamo fino a La Spezia o Levanto e usiamo il panoramico treno costiero — il modo più rapido e rilassato per spostarsi tra loro." },
        { q: "Quanto distano dalla Toscana?", a: "Circa 90 minuti da Lucca o Pisa, e vicinissime dal porto di La Spezia — perfette come escursione a terra o come giornata intera." },
        { q: "Quanti borghi possiamo vedere?", a: "Di solito ne consigliamo due o tre da godere con calma, invece di correre tra tutti e cinque in un giorno." },
        { q: "Possiamo andare in barca?", a: "Nelle giornate calme sì — la barca offre le viste più spettacolari dei borghi dal mare, e puoi prenotarla direttamente." },
      ],
    },
  },
};

export default function CinqueTerreGuide() {
  const { lang } = useLang();
  const c = CONTENT[lang === "it" ? "it" : "en"];
  const { ["@context"]: _ctx, ...serviceNode } = jsonLd;
  const graphLd = { "@context": "https://schema.org", "@graph": [serviceNode, faqPageLd(c.faq.items)] };
  return (
    <GuideLayout
      testid="cinque-terre-guide"
      faq={c.faq}
      seo={{ title: c.seoTitle, description: c.seoDesc, path: "/cinque-terre-private-tour", locale: lang === "it" ? "it_IT" : "en_US", jsonLd: graphLd }}
      overline={c.overline}
      h1={c.h1}
      intro={c.intro}
      heroImage={HERO}
      heroAlt={c.heroAlt}
      blocks={c.blocks}
      cards={c.cards}
      note={c.note}
      cta={c.cta}
    />
  );
}
